import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { FiltersPopover } from '@/shared/components/custom/filters-popover';
import { FloatingLabelDatePicker } from '@/shared/components/custom/form-field/FloatingLabelDatePicker';
import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { membersService } from '@/features/members/public';
import { useUserTimeEntries } from '@/features/time-tracking/public';
import { useOpenTask } from '@/features/tasks/public';
import { TimeEntriesTable } from './TimeEntriesTable';
import { exportTimeEntriesCsv } from '../lib/exportTimeEntriesCsv';

export function UserTimeReport() {
  const { t } = useTranslation('reports');
  const [userId, setUserId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const filters = { from: from ?? undefined, to: to ?? undefined };
  const activeFiltersCount = (from ? 1 : 0) + (to ? 1 : 0);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const listQuery = useUserTimeEntries(userId, {
    page: pageIndex + 1,
    pageSize,
    ...filters,
  });
  const pagination = usePagination({
    totalItems: listQuery.data?.totalCount ?? 0,
    pageIndex,
    onPageChange: setPageIndex,
    pageSize,
    onPageSizeChange: setPageSize,
  });

  const entries = listQuery.data?.items ?? [];
  const openTask = useOpenTask();

  function handleExport() {
    exportTimeEntriesCsv(entries, {
      filename: 'time-report-user.csv',
      headers: [
        t('table.columns.task'),
        t('table.columns.project'),
        t('table.columns.person'),
        t('table.columns.start'),
        t('table.columns.end'),
        t('table.columns.duration'),
        t('table.columns.billable'),
      ],
      billableLabels: { yes: t('table.billableYes'), no: t('table.billableNo') },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Toolbar className="py-0">
        <Toolbar.Actions>
          <AsyncCombobox
            queryKey={['reports-user-options']}
            fetchFn={async ({ search, cursor, pageSize }) => {
              const page = cursor ? Number(cursor) : 1;
              const res = await membersService.getAll({
                SearchTerm: search || undefined,
                Page: page,
                PageSize: pageSize,
              });
              return {
                items: res.items.map((m) => ({
                  id: m.userId ?? m.id,
                  label: m.displayName,
                  description: m.email,
                })),
                total: res.total,
                totalPages: res.totalPages,
                nextCursor: page < res.totalPages ? String(page + 1) : undefined,
              };
            }}
            value={userId}
            onChange={setUserId}
            clearable
            placeholder={t('filters.selectUser')}
            searchPlaceholder={t('filters.search')}
            triggerClassName="w-56"
            data-testid="reports-user-select"
          />

          <FiltersPopover activeCount={activeFiltersCount} label={t('filters.trigger')}>
            <FiltersPopover.Content>
              <FloatingLabelDatePicker
                id="reports-from"
                label={t('filters.from')}
                value={from}
                onChange={setFrom}
                data-testid="reports-from"
              />
              <FloatingLabelDatePicker
                id="reports-to"
                label={t('filters.to')}
                value={to}
                onChange={setTo}
                data-testid="reports-to"
              />
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFrom(null);
                    setTo(null);
                  }}
                  className="w-full"
                >
                  {t('filters.reset')}
                </Button>
              )}
            </FiltersPopover.Content>
          </FiltersPopover>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                onClick={handleExport}
                disabled={entries.length === 0}
                data-testid="reports-export-btn"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('actions.export')}</TooltipContent>
          </Tooltip>
        </Toolbar.Actions>
      </Toolbar>

      {!userId ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('table.selectPrompt')}</p>
      ) : (
        <TimeEntriesTable
          entries={entries}
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          pagination={pagination}
          onTaskClick={openTask}
        />
      )}
    </div>
  );
}
